import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { tasksApi } from '../../api/tasks';
import { toast } from "sonner";

interface TaskDetail {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  completed_at?: string | null;
  progress_percent?: number | null;
}

interface CommentRow {
  id: string;
  user_id: string;
  commenter_name: string;
  comment: string;
  created_at?: string | null;
  edited_at?: string | null;
}

interface ActivityRow {
  id: string;
  action: string;
  message?: string | null;
  actor_name: string;
  created_at?: string | null;
}

interface AttachmentRow {
  id: string;
  file_name?: string | null;
  file_url?: string | null;
  file_size?: number | null;
  mime_type?: string | null;
  uploaded_by_name?: string | null;
  created_at?: string | null;
}

interface ChecklistRow {
  id: string;
  item: string;
  is_done: boolean;
  order_index: number;
  done_at?: string | null;
}

interface ClientTaskDetailProps {
  taskId: string;
  onClose: () => void;
  onChanged?: () => void;
}

function fmtBytes(n?: number | null) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 ring-gray-200',
  TODO: 'bg-blue-100 text-blue-700 ring-blue-200',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
  IN_REVIEW: 'bg-purple-100 text-purple-700 ring-purple-200',
  REVIEW: 'bg-purple-100 text-purple-700 ring-purple-200',
  DONE: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  CANCELLED: 'bg-red-100 text-red-700 ring-red-200',
};

function statusBadge(status?: string | null) {
  if (!status) return 'bg-gray-100 text-gray-600 ring-gray-200';
  return STATUS_BADGE[status.toUpperCase()] || 'bg-gray-100 text-gray-600 ring-gray-200';
}

function fmtDate(s?: string | null) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return s;
  }
}

const ACTIVITY_LABEL: Record<string, string> = {
  CREATED: 'Task created',
  STATUS_CHANGED: 'Status changed',
  COMMENTED: 'New comment',
  COMPLETED: 'Marked complete',
  DRAFT_APPROVED: 'Draft approved',
  DRAFT_REJECTED: 'Draft rejected',
  DRAFT_SENT_BACK: 'Draft sent back',
};

export default function ClientTaskDetail({ taskId, onClose, onChanged }: ClientTaskDetailProps) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [checklist, setChecklist] = useState<ChecklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tasksApi.getTaskClientView(taskId);
      setTask(res.data?.task || null);
      setComments(Array.isArray(res.data?.comments) ? res.data.comments : []);
      setActivity(Array.isArray(res.data?.activity) ? res.data.activity : []);
      setAttachments(Array.isArray(res.data?.attachments) ? res.data.attachments : []);
      setChecklist(Array.isArray(res.data?.checklist) ? res.data.checklist : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || uploading) return;
    setUploading(true);
    try {
      await tasksApi.postClientAttachment(taskId, file);
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const submitComment = async () => {
    const text = reply.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await tasksApi.postClientComment(taskId, text);
      setReply('');
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-stretch justify-end"
      style={{ background: 'rgba(15, 29, 20, 0.45)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Task</p>
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {task?.title || (loading ? 'Loading…' : 'Task')}
            </h2>
            {task && (
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${statusBadge(task.status)}`}>
                  {(task.status || '—').replace(/_/g, ' ')}
                </span>
                {task.due_date && (
                  <span className="text-xs text-gray-500">Due {fmtDate(task.due_date)}</span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading task…</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600 bg-red-50 border-b border-red-100">{error}</div>
        ) : !task ? (
          <div className="p-10 text-center text-gray-500">Task not found.</div>
        ) : (
          <>
            <section className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Description</h3>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {task.description?.trim() || <span className="text-gray-400 italic">No description provided.</span>}
              </p>
            </section>

            <section className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
                Checklist ({checklist.filter(c => c.is_done).length}/{checklist.length})
              </h3>
              {checklist.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No checklist items.</p>
              ) : (
                <ul className="space-y-2">
                  {checklist.map((c) => (
                    <li key={c.id} className="flex items-start gap-2 text-sm">
                      <span
                        className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border ${
                          c.is_done
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-gray-300'
                        }`}
                      >
                        {c.is_done && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className={c.is_done ? 'line-through text-gray-500' : 'text-gray-800'}>
                        {c.item}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                  Attachments ({attachments.length})
                </h3>
                <button
                  type="button"
                  onClick={onPickFile}
                  disabled={uploading}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                >
                  {uploading ? 'Uploading…' : '+ Attach file'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={onFileChosen}
                />
              </div>
              {attachments.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No attachments yet.</p>
              ) : (
                <ul className="space-y-2">
                  {attachments.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center gap-3 text-sm border border-gray-200 rounded-lg p-2.5"
                    >
                      <div className="flex-1 min-w-0">
                        {a.file_url ? (
                          <a
                            href={a.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-emerald-700 hover:underline truncate block"
                          >
                            {a.file_name || 'file'}
                          </a>
                        ) : (
                          <span className="font-medium text-gray-900 truncate block">
                            {a.file_name || 'file'}
                          </span>
                        )}
                        <div className="text-xs text-gray-500">
                          {fmtBytes(a.file_size)} · {a.uploaded_by_name || 'Team'} · {fmtDate(a.created_at)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">Activity</h3>
              {activity.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No activity yet.</p>
              ) : (
                <ul className="space-y-2">
                  {activity.map((a) => (
                    <li key={a.id} className="text-sm text-gray-700 flex gap-2">
                      <span className="text-gray-400 text-xs whitespace-nowrap mt-0.5">{fmtDate(a.created_at)}</span>
                      <span>
                        <span className="font-medium text-gray-900">{a.actor_name}</span>{' '}
                        <span className="text-gray-600">— {ACTIVITY_LABEL[a.action] || a.action.toLowerCase().replace(/_/g, ' ')}</span>
                        {a.message && <span className="text-gray-500 italic"> · {a.message}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="px-6 py-5">
              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
                Comments ({comments.length})
              </h3>
              {comments.length === 0 ? (
                <p className="text-sm text-gray-500 italic mb-4">No comments yet. Be the first to reply.</p>
              ) : (
                <ul className="space-y-3 mb-4">
                  {comments.map((c) => (
                    <li key={c.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900">{c.commenter_name}</span>
                        <span className="text-xs text-gray-500">{fmtDate(c.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.comment}</p>
                    </li>
                  ))}
                </ul>
              )}

              <div className="border-t border-gray-200 pt-3">
                <label htmlFor="reply" className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                  Post a reply
                </label>
                <textarea
                  id="reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  placeholder="Write a comment for the team…"
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    disabled={!reply.trim() || posting}
                    onClick={submitComment}
                    className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {posting ? 'Posting…' : 'Post comment'}
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
